import { Helmet } from "react-helmet-async";
import { LoadingPage } from "views";
import Header from "modules/Header";
import Footer from "modules/Footer";
import ReactDOM from "react-dom";
import { useParams } from "react-router";
import { useLanguage } from "context/languageContext";
import { getFilteredPlaces } from "services/getFilteredPlaces";
import LandingPageSlider from "./components/LandingPageSlider";
import LandingPageMap from "./components/LandingPageMap";
import React, { useEffect, useState, useCallback, useRef, memo } from "react";
import getPlacesByProviderId from "services/getPlacesByProviderId";
import { getLandingPageByKey } from "services/landingPage.services";
import "./styles.scss";
import useQueryParam from "hooks/useQueryParam";
import LandingPageUsagesList from "./components/LandingPageUsagesList";
import { parseUsageTypesFromContent } from "./components/LandingPageUsagesList/filterUsages";

// Add this after all your imports
// ------------------------------------------------------------------
// 🌍 LANGUAGE CONFIGURATION for multi-domain SEO
// ------------------------------------------------------------------
const LANGUAGE_CONFIG = {
  fr: { 
    domain: 'https://www.chooseandwork.fr', 
    locale: 'fr_FR', 
    hreflang: 'fr',
    language: 'French'
  },
  es: { 
    domain: 'https://www.chooseandwork.es', 
    locale: 'es_ES', 
    hreflang: 'es',
    language: 'Spanish'
  },
  en: { 
    domain: 'https://www.chooseandwork.com', 
    locale: 'en_US', 
    hreflang: 'en',
    language: 'English'
  },
  uk: { 
    domain: 'https://www.chooseandwork.uk', 
    locale: 'en_GB', 
    hreflang: 'en-GB',
    language: 'English (UK)'
  },
  be: { 
    domain: 'https://www.chooseandwork.be', 
    locale: 'nl_BE', 
    hreflang: 'nl',
    language: 'Dutch (Belgium)'
  }
};

// ------------------------------------------------------------------
// 🔒 MEMOIZED CONTENT – only re-renders when processedContent changes
//    Handles Pipedrive forms reliably on refresh and navigation
// ------------------------------------------------------------------
const MemoizedHTMLContent = memo(({ html }) => {
  const containerRef = useRef(null);
  const loadedForms = useRef(new Set());

  // Load and initialize a single form
  const loadForm = (formDiv) => {
    const url = formDiv.dataset.pdWebforms;
    if (!url || loadedForms.current.has(url)) return;
    loadedForms.current.add(url);

    const script = document.createElement("script");
    script.src = url.includes("pipedrivewebforms.com")
      ? "https://cdn.pipedriveassets.com/web-form-assets/webforms.min.js"
      : "https://webforms.pipedrive.com/f/loader";
    script.async = true;
    script.onload = () => {
      if (window.PDForms?.init) {
        window.PDForms.init(formDiv);
      } else if (window.PDWebForms?.init) {
        window.PDWebForms.init(formDiv);
      }
    };
    document.body.appendChild(script);
  };

  // Initialize all forms inside the container
  const initAllForms = () => {
    if (!containerRef.current) return;
    containerRef.current.querySelectorAll(".pipedriveWebForms").forEach(loadForm);
  };

  useEffect(() => {
    initAllForms();

    // Observe dynamically added forms
    const observer = new MutationObserver(() => initAllForms());
    observer.observe(containerRef.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [html]);

  // Remove scripts from innerHTML to prevent React ignoring them
  const sanitizedHTML = html.replace(/<script[\s\S]*?<\/script>/gi, "");

  return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />;
});

// ------------------------------------------------------------------
// 🚀 Main LandingPage Component
// ------------------------------------------------------------------
const LandingPage = () => {
  const externalId = useQueryParam("externalID");
  const { landingPageId } = useParams();
  const [mainColor, setMainColor] = useState();
  const [hideHeader, setHideHeader] = useState(false);
  const [hideFooter, setHideFooter] = useState(false);
  const { language } = useLanguage();
  const [content, setContent] = useState(null);
  const [processedContent, setProcessedContent] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [fetchedPlaces, setFetchedPlaces] = useState(null);
  const [cities, setCities] = useState([]);
  const [sliderContainer, setSliderContainer] = useState(null);
  const [mapContainer, setMapContainer] = useState(null);
  const [usageTypes, setUsageTypes] = useState([]);
  const [usageContainerId, setUsageContainerId] = useState(null);

  // SEO states
  const [pageTitle, setPageTitle] = useState("");
  const [pageMetaTitle, setPageMetaTitle] = useState("");
  const [pageMetaDescription, setPageMetaDescription] = useState("");
  const [pageMetaKeywords, setPageMetaKeywords] = useState("");

  // ------------------------------------------------------------------
  // 1. Fetch landing page content
  // ------------------------------------------------------------------
  useEffect(() => {
    const fetchLandingPage = async () => {
      setErrorMessage(null);
      try {
        const landingPageContent = await getLandingPageByKey(landingPageId, language);

        if (!landingPageContent) {
          setContent(<div></div>);
          return;
        }

        if (!landingPageContent.body) {
          setContent(<div></div>);
          return;
        }

        setContent(landingPageContent);
        setHideHeader(landingPageContent.hide_header);
        setHideFooter(landingPageContent.hide_footer);

        setPageTitle(landingPageContent.title || "");
        setPageMetaTitle(landingPageContent.meta_title || "");
        setPageMetaDescription(landingPageContent.meta_description || "");
        setPageMetaKeywords(landingPageContent.meta_keywords || "");

        if (landingPageContent.color?.trim()) {
          updateCSSVariables(landingPageContent.color);
          setMainColor(landingPageContent.color);
        }

        await processContent(landingPageContent.body);
      } catch (error) {
        setErrorMessage(error.message || "An error occurred while loading the page.");
      }
    };

    fetchLandingPage();
  }, [landingPageId, language]);

  // ------------------------------------------------------------------
  // 2. CSS variable update
  // ------------------------------------------------------------------
  const updateCSSVariables = (color) => {
    document.documentElement.style.setProperty("--main-color", color);
    document.documentElement.style.setProperty("--navColor", color);
  };

  // ------------------------------------------------------------------
  // 3. Process HTML body: replace custom tags, and STRIP inline scripts
  //    from legacy Pipedrive divs.
  // ------------------------------------------------------------------
  const processContent = useCallback(async (body) => {
    let processedBody = body;

    // ---- Locations (map) ----
    const locationsMatch = processedBody.match(/\-\-\-locations\-\-\-([\s\S]*?)\-\-\-\/locations\-\-\-/);
    if (locationsMatch) {
      processedBody = await handleLocations(locationsMatch[0], locationsMatch[1], processedBody);
    }

    // ---- Static carousel (filtered places) ----
    const staticMatch = processedBody.match(/\{{([^}]+)\}}/);
    if (staticMatch) {
      processedBody = await handleStaticCarousel(staticMatch[0], processedBody);
    }

    // ---- Dynamic carousel (by provider ID) ----
    const dynamicMatch = processedBody.match(/--(\d{4})(?:\.(.+?))?--/);
    if (dynamicMatch) {
      processedBody = await handleDynamicContent(dynamicMatch[0], processedBody);
    }

    // ---- Parse usage types from content ----
    const { types } = parseUsageTypesFromContent(processedBody);
    if (types && types.length > 0) {
      setUsageTypes(types);
      // Generate a unique container ID for this usage section
      const containerId = `landing-page-usage-container-${Date.now()}`;
      setUsageContainerId(containerId);
      // Replace the usage tag with a container div
      processedBody = processedBody.replace(
        /\-\-\-usages\-\-\-[\s\S]*?\-\-\-\/usages\-\-\-/, 
        `<div id="${containerId}"></div>`
      );
    }

    // ---- Strip scripts from pipedrive divs ----
    processedBody = processedBody.replace(
      /(<div\s+class="pipedriveWebForms"\s+data-pd-webforms="[^"]+"[^>]*>)\s*<script[^>]*>[\s\S]*?<\/script>\s*<\/div>/gi,
      "$1</div>"
    );

    setProcessedContent(processedBody);
  }, []);

  // ------------------------------------------------------------------
  // 4. Handlers for locations, carousels (unchanged)
  // ------------------------------------------------------------------
  const handleLocations = (fullMatch, locationsContent, body) => {
    try {
      try {
        const cities = JSON.parse(locationsContent.trim());
        if (Array.isArray(cities)) {
          setCities(cities);
          return body.replace(fullMatch, '<div id="locations-map-container"></div>');
        }
      } catch (e) {
        const cities = [];
        locationsContent.trim().split("\n").forEach((line) => {
          const [name, lat, lng] = line.split("|").map((item) => item.trim());
          if (name && lat && lng) {
            cities.push({ name, lat: parseFloat(lat), lng: parseFloat(lng) });
          }
        });
        if (cities.length > 0) {
          setCities(cities);
          return body.replace(fullMatch, '<div id="locations-map-container"></div>');
        }
      }
    } catch (error) {}
    return body.replace(fullMatch, "");
  };

  const handleStaticCarousel = async (match, body) => {
    try {
      const [usage, style, city, postalCode, country] = match.replace(/{{|}}/g, "").split("|");
      const data = await getFilteredPlaces({
        usages: [usage],
        style,
        city,
        postal_code: postalCode,
        country,
      });
      setFetchedPlaces(data.data);
      return body.replace(match, "<div id='landing-page-slider-container'></div>");
    } catch (error) {
      return body.replace(match, "");
    }
  };

  const handleDynamicContent = async (match, body) => {
    try {
      const providerId = match.replace(/--/g, "");
      const fetchedPlaces = await getPlacesByProviderId(providerId);
      setFetchedPlaces(fetchedPlaces);
      return body.replace(match, "<div id='landing-page-slider-container'></div>");
    } catch (error) {
      return body.replace(match, "");
    }
  };

  // ------------------------------------------------------------------
  // 5. After processedContent is set, find slider & map containers
  // ------------------------------------------------------------------
  useEffect(() => {
    const sliderEl = document.getElementById("landing-page-slider-container");
    const mapEl = document.getElementById("locations-map-container");

    if (sliderEl) setSliderContainer(sliderEl);
    if (mapEl) setMapContainer(mapEl);
  }, [processedContent]);

  // ------------------------------------------------------------------
  // 6. SEO values
  // ------------------------------------------------------------------
  const seoTitle = pageMetaTitle || pageTitle || "Choose&Work";
  const seoDescription =
    pageMetaDescription ||
    "Meeting rooms, private offices, coworking spaces and virtual offices. Find your perfect workspace with Choose&Work.";
  const seoKeywords =
    pageMetaKeywords || "coworking, meeting rooms, private offices, virtual offices, workspace";

const currentPath = window.location.pathname;
const currentDomain = window.location.origin; // Gets current domain dynamically
const canonicalUrl = `${currentDomain}${currentPath}`;

// Helper function to generate language-specific URLs
const getLanguageUrl = (langCode) => {
  const config = LANGUAGE_CONFIG[langCode];
  if (!config) return null;
  return `${config.domain}${currentPath}`;
};

// Get all available language codes
const allLanguages = Object.keys(LANGUAGE_CONFIG);

// Default/primary language (usually .com)
const defaultLanguage = 'en';

  // ------------------------------------------------------------------
  // 7. Loading & error states
  // ------------------------------------------------------------------
  if (!content && !errorMessage) return <LoadingPage />;
  if (errorMessage) return <div className="error-message">{errorMessage}</div>;

  // ------------------------------------------------------------------
  // 8. Render
  // ------------------------------------------------------------------
  return (
    <>
     <Helmet htmlAttributes={{ lang: language || 'en' }}>
      {/* Basic SEO */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="robots" content="index, follow" />
      
      {/* CANONICAL - points to ITSELF (current domain) */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* HREFLANG - lists ALL 5 language versions */}
      {allLanguages.map(langCode => {
        const url = getLanguageUrl(langCode);
        if (!url) return null;
        return (
          <link 
            key={langCode}
            rel="alternate" 
            hreflang={LANGUAGE_CONFIG[langCode].hreflang} 
            href={url} 
          />
        );
      })}
      
      {/* X-DEFAULT - points to your primary/fallback version (.com) */}
      <link 
        rel="alternate" 
        hreflang="x-default" 
        href={getLanguageUrl(defaultLanguage)} 
      />
      
      {/* Open Graph tags */}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={LANGUAGE_CONFIG[language]?.locale || 'en_US'} />
      
      {/* Twitter Card tags */}
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>

      <section className="landing-page">
        {!hideHeader && <Header />}
        <div className="row">
          <div className="landing-content-col">
            <div id="landing-page">
              {/* 🔥 MEMOIZED CONTENT with robust Pipedrive form initialization */}
              <MemoizedHTMLContent html={processedContent} />

              {/* 📋 USAGE TYPES - Renders at the position where the tag was placed */}
              {usageTypes && usageTypes.length > 0 && usageContainerId && (
                <LandingPageUsagesList 
                  usageTypes={usageTypes}
                  containerId={usageContainerId}
                />
              )}

              {/* 🗺️ Map portal */}
              {mapContainer && cities.length > 0 &&
                ReactDOM.createPortal(
                  <LandingPageMap
                    cities={cities}
                    themeColor={mainColor?.trim() ? mainColor : null}
                  />,
                  mapContainer
                )}

              {/* 🎠 Slider portal */}
              {sliderContainer && fetchedPlaces &&
                ReactDOM.createPortal(
                  <LandingPageSlider
                    spaces={fetchedPlaces}
                    themeColor={mainColor?.trim() ? mainColor : null}
                    forMemberPlus={true}
                    externalId={externalId}
                  />,
                  sliderContainer
                )}
            </div>
          </div>
        </div>
        {!hideFooter && <Footer />}
      </section>
    </>
  );
};

export default LandingPage;